import React, {useState} from 'react';
import {Image, Platform, StyleSheet, TouchableOpacity, View} from 'react-native';
import {
  Asset,
  Callback,
  CameraOptions,
  ImagePickerResponse,
  launchCamera,
  launchImageLibrary,
} from 'react-native-image-picker';
import {Toast} from 'react-native-toast-message/lib/src/Toast';

import {AirtablePromotionItem} from '../models/airtable';
import theme from '../style/theme';
import {getTermsAndConditionsUrl} from '../utils/competitions';
import {isSummerCashGrabCompetition} from '../utils/games/summerCashGrab';
import {logger} from '../utils/logger';
import {requestCameraAccessPermission} from '../utils/permissions/permissions';
import {getEnterHereButtonText} from '../utils/promotions';

import Text from './ui-controls/Text';
import CheckboxComponent from './ui-controls/web/CheckBox';
import OpenURLButton from './OpenURLButton';
import PromotionCaptureExtraUserInput from './PromotionCaptureExtraUserInput';
import {ERROR_MESSAGE} from './ToastBottom';

const MAX_RECEIPT_IMAGE_SIZE_BYTES = 5 * 1024 * 1024; // 5MB
const MAX_RECEIPT_IMAGE_SIZE_MB = 5;

function estimateAssetFileSize(asset: Asset): number | undefined {
  if (asset.fileSize != null) {
    return asset.fileSize;
  }
  // On web, react-native-image-picker often omits fileSize.
  // Estimate from base64 data URI: actual bytes ≈ base64 length × 3/4
  if (Platform.OS === 'web' && asset.uri?.startsWith('data:')) {
    const base64Part = asset.uri.split(',')[1];
    if (base64Part) {
      const padding = (base64Part.match(/=+$/) || [''])[0].length;
      return Math.floor((base64Part.length * 3) / 4) - padding;
    }
  }
  return undefined;
}

type ButtonProps = {
  text: string;
  onPress: () => void;
  disabled?: boolean;
};

function Button({text, onPress, disabled = false}: ButtonProps) {
  const styles = StyleSheet.create({
    buttonOuter: {
      alignItems: 'center',
    },
    button: {
      borderRadius: 4,
      backgroundColor: theme.colour.background.button.primary,
      paddingHorizontal: 12,
      paddingVertical: 8,
    },
    enterText: {
      fontSize: 16,
      color: 'white',
    },
    disabled: {
      opacity: 0.3,
    },
  });
  return (
    <View style={styles.buttonOuter}>
      <TouchableOpacity
        style={disabled ? [styles.button, styles.disabled] : styles.button}
        onPress={onPress}
        disabled={disabled}>
        <Text style={styles.enterText}>{text}</Text>
      </TouchableOpacity>
    </View>
  );
}

type CheckBoxProps = {
  checked: boolean;
  onChange: () => void;
  text: string;
  uncheckedStyles?: {textStyle: Record<string, unknown>};
};

function CheckBox({
  checked = false,
  onChange,
  text,
  uncheckedStyles = {textStyle: {}},
}: CheckBoxProps) {
  const styles = StyleSheet.create({
    checkboxContainer: {
      flexDirection: 'row',
    },
    checkbox: {
      alignSelf: 'center',
    },
    label: {
      margin: 8,
    },
  });
  return (
    <View style={styles.checkboxContainer}>
      <CheckboxComponent
        boxType="square"
        disabled={false}
        value={checked}
        onChange={onChange}
        style={styles.checkbox}
        lineWidth={1}
      />
      <Text
        variant="caption"
        style={checked ? styles.label : [styles.label, uncheckedStyles.textStyle]}>
        {text}
      </Text>
    </View>
  );
}

type MandatoryCheckBoxProps = {
  checked: boolean;
  onChange: () => void;
  text: string;
};

function MandatoryCheckBox(props: MandatoryCheckBoxProps) {
  return <CheckBox {...props} uncheckedStyles={{textStyle: {color: 'red'}}} />;
}

type ImageUploadProps = {
  onUpload: (image: Asset) => void;
  onError?: (error: unknown) => void;
  cameraUploadText?: string;
  pictureGalleryText?: string;
};

function ImageUpload({
  onUpload,
  onError = () => {},
  cameraUploadText = 'Upload from Camera',
  pictureGalleryText = 'Upload image from gallery',
}: ImageUploadProps) {
  const styles = StyleSheet.create({
    container: {
      flex: 1,
      margin: 10,
      flexDirection: 'column',
      alignItems: 'center',
      // alignContent: "stretch",
    },
    containerItem: {
      padding: 10,
      // width: "100%"
    },
  });

  type Uploader = (
    options: CameraOptions,
    callback?: Callback | undefined,
  ) => Promise<ImagePickerResponse>;

  function handleImagePress(uploader: Uploader) {
    uploader({mediaType: 'photo'})
      .then(result => {
        logger.debug('PromotionCaptureDetails', 'Image upload result received', {
          hasAssets: !!result.assets,
        });
        if (result.assets) {
          const asset = result.assets[0];
          const fileSize = estimateAssetFileSize(asset);
          if (fileSize != null && fileSize > MAX_RECEIPT_IMAGE_SIZE_BYTES) {
            Toast.show({
              type: ERROR_MESSAGE,
              text1: `The selected file is too large. Please choose an image under ${MAX_RECEIPT_IMAGE_SIZE_MB}MB.`,
            });
            return;
          }
          onUpload(asset);
        }
      })
      .catch((error: unknown) => {
        logger.error('PromotionCaptureDetails', 'Image upload error', {error});
        onError(error);
      });
  }
  const handleCameraUpload = async () => {
    let authorised = false;
    try {
      authorised = await requestCameraAccessPermission();
    } catch (error) {
      logger.error('PromotionCaptureDetails', 'Camera permission request error', {error});
    }
    if (authorised) {
      // @ts-expect-error TODO: check typings when updating this code
      handleImagePress.bind(this, launchCamera)();
    } else {
      Toast.show({type: ERROR_MESSAGE, text1: 'Camera permission denied'});
    }
  };

  return (
    <View style={styles.container}>
      {Platform.OS !== 'web' && (
        <View style={styles.containerItem}>
          <Button text={cameraUploadText} onPress={handleCameraUpload} />
        </View>
      )}
      <Button
        text={pictureGalleryText}
        // @ts-expect-error TODO: check typings when updating this code
        onPress={handleImagePress.bind(this, launchImageLibrary)}
      />
    </View>
  );
}

function OnlineOrderView({activation}: {activation: AirtablePromotionItem}) {
  if (activation.game) {
    return null;
  }

  // for some reason Airtable is keeping a "\n" after the cell content is
  // cleared
  const adhocPromotionMessage = activation.adhocPromotionMessage?.replace(/(\r\n|\n|\r)/gm, '');
  const styles = StyleSheet.create({
    container: {
      justifyContent: 'space-around',
      alignItems: 'center',
      backgroundColor: '#c5edc5',
      height: 90,
      padding: 10,
    },
  });
  return (
    <View style={styles.container}>
      {adhocPromotionMessage ? (
        <Text>{adhocPromotionMessage}</Text>
      ) : (
        <>
          <Text>CONGRATULATIONS!</Text>
          <Text>YOU HAVE QUALIFIED TO ENTER</Text>
          <Text>THE DRAW</Text>
        </>
      )}
    </View>
  );
}

export type PromotionUserSubmission = {
  isTermsAccepted: boolean;
  isOptInLmgMarketing: boolean;
  isOptInSupplierMarketing: boolean;
  receiptImage: Asset | null;
  extraUserInputType: string;
  extraUserInput: string;
  isReceiptRequired: boolean;
};

type PromotionCaptureDetailsProps = {
  activation: AirtablePromotionItem;
  isReceiptRequired: boolean;
  onCancel?: () => void;
  onSubmit: (data: PromotionUserSubmission) => void;
  orderNumber?: string;
  isLastCompetitionRemaining: boolean; // Summer Cash Grab
};

export default function PromotionCaptureDetails({
  activation,
  onSubmit,
  orderNumber,
  onCancel = () => {},
  isReceiptRequired,
  isLastCompetitionRemaining,
}: PromotionCaptureDetailsProps) {
  const [receiptImage, setReceiptImage] = useState<Asset | null>(null);
  const isReceiptUploaded = !!receiptImage;

  const [isTermsAccepted, setIsTermsAccepted] = useState(false);
  const [extraUserInput, setExtraUserInput] = useState<unknown>(null);
  const [isOptInLmgMarketing, setIsOptInLmgMarketing] = useState(true);
  const [isOptInSupplierMarketing, setIsOptInSupplierMarketing] = useState(false);

  const isOnlineOrder = !!orderNumber;

  const handleTermsAcceptedCheckboxChange = () => setIsTermsAccepted(!isTermsAccepted);
  const handleMarketingCheckboxChange = () => setIsOptInLmgMarketing(!isOptInLmgMarketing);
  const handleSupplierMarketingCheckboxChange = () =>
    setIsOptInSupplierMarketing(!isOptInSupplierMarketing);
  const handleExtraUserInputChange = (value: unknown) => setExtraUserInput(value);

  // Spec: /doc/solutions/competitions.md
  const extraUserInputSpec = activation.extraUserInputSpec;
  const termsAndConditionsUrl = getTermsAndConditionsUrl(activation);

  // Temporary: Summer Cash Grab
  const isSummerCashGrab = isSummerCashGrabCompetition(activation);
  const isLastCompetitionRemainingIfSummerCashGrab = isSummerCashGrab
    ? isLastCompetitionRemaining
    : true;
  //-----------------------------

  const isExtraUserInputEnteredIfMandatory =
    extraUserInputSpec && extraUserInputSpec.mandatory ? extraUserInput : true;

  let isSubmitEnabled = false;
  if (activation.game) {
    isSubmitEnabled = true;
  } else {
    isSubmitEnabled =
      !!isTermsAccepted &&
      !!isExtraUserInputEnteredIfMandatory &&
      !!isLastCompetitionRemainingIfSummerCashGrab;
  }

  //-----------------------------

  const handleSubmit = () =>
    onSubmit({
      isTermsAccepted,
      isOptInLmgMarketing,
      isOptInSupplierMarketing,
      receiptImage,
      extraUserInputType: extraUserInputSpec && extraUserInputSpec.type,
      extraUserInput: JSON.stringify(extraUserInput),
      isReceiptRequired,
    });

  const isReceiptUploadRequired = isReceiptRequired && !isReceiptUploaded && !isOnlineOrder;

  return isReceiptUploadRequired ? (
    <ImageUpload
      onUpload={setReceiptImage}
      cameraUploadText="Upload Receipt from Camera"
      pictureGalleryText="Upload Receipt from Gallery "
    />
  ) : (
    <View style={{flex: 1, justifyContent: 'space-between', marginTop: 10}}>
      <View>
        {isOnlineOrder ? (
          <OnlineOrderView activation={activation} />
        ) : (
          isReceiptRequired && (
            <View
              style={{
                flexDirection: 'row',
                padding: 10,
                alignItems: 'center',
                justifyContent: 'flex-start',
                backgroundColor: '#c5edc5',
              }}>
              <Image
                source={{uri: receiptImage?.uri}}
                style={{
                  width: 50,
                  height: 50,
                  marginRight: 10,
                  borderRadius: 10,
                }}
                resizeMethod="scale"
              />
              <Text variant="caption">RECEIPT UPLOADED SUCCESSFULLY</Text>
            </View>
          )
        )}
        {!activation.game && (
          <View style={{flex: 1, justifyContent: 'space-between', margin: 10}}>
            <View
              style={{
                paddingVertical: 5,
                flexDirection: 'row',
                alignItems: 'center',
              }}>
              <MandatoryCheckBox
                text="AGREE TO TERMS AND CONDITIONS"
                checked={isTermsAccepted}
                onChange={handleTermsAcceptedCheckboxChange}
              />
              {!!termsAndConditionsUrl && (
                <OpenURLButton url={termsAndConditionsUrl}>(READ)</OpenURLButton>
              )}
            </View>
            <View style={{paddingVertical: 5}}>
              <CheckBox
                text="OPT IN TO LMG MARKETING"
                checked={isOptInLmgMarketing}
                onChange={handleMarketingCheckboxChange}
              />
            </View>
            <View style={{paddingVertical: 5}}>
              <CheckBox
                text="OPT IN TO SUPPLIER MARKETING"
                checked={isOptInSupplierMarketing}
                onChange={handleSupplierMarketingCheckboxChange}
              />
            </View>
            {!!extraUserInputSpec && (
              <PromotionCaptureExtraUserInput
                extraUserInputSpec={extraUserInputSpec}
                onExtraUserInputChanged={handleExtraUserInputChange}
              />
            )}
          </View>
        )}
      </View>
      <View style={{flex: 1, width: '100%', paddingBottom: 20}}>
        {!!isSummerCashGrab && !!isTermsAccepted && !isLastCompetitionRemaining && (
          <Text style={{marginBottom: 8}}>
            Please enter or skip the remaining competitions first
          </Text>
        )}
        <Button
          disabled={!isSubmitEnabled}
          text={getEnterHereButtonText(activation)}
          onPress={handleSubmit}
        />
      </View>
      {!!orderNumber && (
        <View style={{flex: 1, width: '100%', marginTop: 16}}>
          <Button text="SKIP ENTERING" onPress={onCancel} />
        </View>
      )}
    </View>
  );
}
